package com.xtream.composeapp.presentation.ui.tvchannels

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyItemScope
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameMillis
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import com.xtream.ui.ui.util.extensions.lightenColor

class ReorderableLazyColumnState(
    val lazyListState: LazyListState,
    private val onMove: (fromIndex: Int, toIndex: Int) -> Unit
) {
    var draggedKey by mutableStateOf<Any?>(null)
        private set
    var draggedIndex by mutableStateOf<Int?>(null)
        private set
    var isPointerDrag by mutableStateOf(false)
        private set
    var initialTouchOffsetInItem by mutableFloatStateOf(0f)
        private set
    var currentPointerY by mutableFloatStateOf(0f)
        private set
    // Tracks the last known screen Y of the dragged item's top edge
    var lastKnownItemTopY by mutableFloatStateOf(0f)
        internal set

    fun startPointerDrag(key: Any, index: Int, itemOffset: Int, initialY: Float) {
        draggedKey = key
        draggedIndex = index
        isPointerDrag = true
        initialTouchOffsetInItem = initialY - itemOffset
        currentPointerY = initialY
        lastKnownItemTopY = itemOffset.toFloat()
    }

    fun updatePointerDrag(dragAmountY: Float) {
        currentPointerY += dragAmountY
        checkAndSwapItems()
    }

    private var isSwapPending by mutableStateOf(false)

    fun checkAndSwapItems() {
        val currentDraggedKey = draggedKey ?: return
        val currentDraggedIndex = draggedIndex ?: return
        val visibleItems = lazyListState.layoutInfo.visibleItemsInfo

        val currentDraggedItemInfo = visibleItems.firstOrNull { it.key == currentDraggedKey }

        if (currentDraggedItemInfo != null) {
            lastKnownItemTopY = currentDraggedItemInfo.offset.toFloat()

            // If we were waiting for a swap to finish, check if the item arrived at its new index layout
            if (isSwapPending) {
                if (currentDraggedItemInfo.index == currentDraggedIndex) {
                    isSwapPending = false // Unlock calculations!
                } else {
                    return // Still waiting for layout processing to catch up
                }
            }
        } else if (isSwapPending) {
            // If the item is culled due to scroll but we are pending, keep waiting
            return
        }

        val itemHeight = currentDraggedItemInfo?.size?.toFloat() ?: 80f
        val desiredTop = currentPointerY - initialTouchOffsetInItem
        val desiredCenter = desiredTop + (itemHeight / 2f)

        val targetItem = visibleItems.firstOrNull { itemInfo ->
            itemInfo.key != currentDraggedKey &&
                    itemInfo.index != currentDraggedIndex &&
                    desiredCenter.toInt() in itemInfo.offset..(itemInfo.offset + itemInfo.size)
        }

        if (targetItem != null && !isSwapPending) {
            isSwapPending = true // Lock any further calculations
            onMove(currentDraggedIndex, targetItem.index)
            draggedIndex = targetItem.index
        }
    }



    fun startDpadSelection(key: Any, index: Int) {
        draggedKey = key
        draggedIndex = index
        isPointerDrag = false
    }

    fun stopDrag() {
        draggedKey = null
        draggedIndex = null
        isPointerDrag = false
        isSwapPending = false // Reset lock
    }
    fun moveUp(currentIndex: Int) {
        if (currentIndex > 0) {
            onMove(currentIndex, currentIndex - 1)
            draggedIndex = currentIndex - 1
        }
    }

    fun moveDown(currentIndex: Int) {
        val totalItems = lazyListState.layoutInfo.totalItemsCount
        if (currentIndex < totalItems - 1) {
            onMove(currentIndex, currentIndex + 1)
            draggedIndex = currentIndex + 1
        }
    }
}

@Composable
fun rememberReorderableLazyColumnState(
    lazyListState: LazyListState = rememberLazyListState(),
    onMove: (fromIndex: Int, toIndex: Int) -> Unit
): ReorderableLazyColumnState {
    return remember(lazyListState, onMove) {
        ReorderableLazyColumnState(lazyListState, onMove)
    }
}

/**
 * The slot-based list layout container. Completely detached from item types.
 */
@Composable
fun ReorderableLazyColumn(
    state: ReorderableLazyColumnState,
    modifier: Modifier = Modifier,
    autoScrollThresholdTop: Dp = 40.dp,
    autoScrollThresholdBottom: Dp = 40.dp,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    // The floating clone — caller provides the composable for the dragged item
    floatingDragContent: (@Composable () -> Unit)? = null,
    content: LazyListScope.() -> Unit
) {
    val density = LocalDensity.current
    val thresholdTopPx = with(density) { autoScrollThresholdTop.toPx() }
    val thresholdBottomPx = with(density) { autoScrollThresholdBottom.toPx() }

    LaunchedEffect(state.draggedKey, state.currentPointerY) {
        if (state.draggedKey != null && state.isPointerDrag) {
            while (true) {
                val layoutInfo = state.lazyListState.layoutInfo
                val viewportHeight = layoutInfo.viewportEndOffset
                val scrollAmount = when {
                    state.currentPointerY < thresholdTopPx -> -50f // auto scroll speed
                    state.currentPointerY > viewportHeight - thresholdBottomPx -> 50f
                    else -> 0f
                }
                if (scrollAmount != 0f) {
                    val consumed = state.lazyListState.scrollBy(scrollAmount)
                    if (consumed != 0f) state.checkAndSwapItems()
                }
                withFrameMillis { }
            }
        }
    }

    // Outer Box: both LazyColumn and floating overlay live here
    Box(modifier = modifier) {
        LazyColumn(
            state = state.lazyListState,
            contentPadding = contentPadding,
            modifier = Modifier
                .fillMaxSize()
                .pointerInput(state) {
                    detectDragGesturesAfterLongPress(
                        onDragStart = { offset ->
                            state.lazyListState.layoutInfo.visibleItemsInfo
                                .firstOrNull { item ->
                                    offset.y.toInt() in item.offset..(item.offset + item.size)
                                }
                                ?.also { itemInfo ->
                                    state.startPointerDrag(
                                        key = itemInfo.key,
                                        index = itemInfo.index,
                                        itemOffset = itemInfo.offset,
                                        initialY = offset.y
                                    )
                                }
                        },
                        onDrag = { change, dragAmount ->
                            change.consume()
                            state.updatePointerDrag(dragAmount.y)
                        },
                        onDragEnd = { state.stopDrag() },
                        onDragCancel = { state.stopDrag() }
                    )
                },
            content = content
        )

        // ── Floating overlay clone ─────────────────────────────────────────
        // Lives outside LazyColumn so it's never clipped or recycled by scroll
        if (state.isPointerDrag && state.draggedKey != null && floatingDragContent != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .graphicsLayer {
                        translationY = state.currentPointerY - state.initialTouchOffsetInItem
                        shadowElevation = 8.dp.toPx()
                    }
                    .zIndex(10f)
            ) {
                floatingDragContent()
            }
        }
    }
}
/**
 * The customizable Item wrapper handling graphics layers, focus states, and key conversions.
 */
@OptIn(ExperimentalFoundationApi::class)
@Composable
fun LazyItemScope.ReorderableItem(
    state: ReorderableLazyColumnState,
    key: Any,
    index: Int,
    modifier: Modifier = Modifier,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    content: @Composable (isDragging: Boolean) -> Unit
) {
    val isDragging = key == state.draggedKey
    val animationModifier = if (isDragging) Modifier else Modifier.animateItem()
    val isFocused by interactionSource.collectIsFocusedAsState()

// 2. Compute the background color dynamically based on selection and focus states
    val containerColor = if (isFocused) {
        MaterialTheme.colorScheme.onSurface
    } else {
        MaterialTheme.colorScheme.surfaceVariant.lightenColor(if (isDragging) 0.1f else 0f)
    }



    Box(
        modifier = modifier
            .background(containerColor)
            .then(animationModifier)
            .zIndex( if (isDragging ) 1f else 0f)
            .graphicsLayer {
                // Hide the original slot while dragging — overlay clone is the visual
                alpha = if (isDragging && state.isPointerDrag) 0f else 1f

                // No translationY here anymore — that's the overlay's job
            }
            //.focusable(interactionSource = interactionSource)
            .onPreviewKeyEvent { keyEvent ->
                // 1. Extract platform-agnostic Key and EventType representations
                val currentKey = keyEvent.key
                //note web only supports key up
                val isKeyUp =/*currentPlatform == PlatformType.WEB
                        &&*/ keyEvent.type ==KeyEventType.KeyUp
                val isKeyDown =
                    keyEvent.type ==KeyEventType.KeyDown

                println("+++KEY: $currentKey $isKeyDown")

                     when (currentKey) {
                    Key.DirectionCenter, Key.Enter, Key.ButtonSelect -> {

                        if ( isKeyUp ) {
                            if (state.draggedKey == null) state.startDpadSelection(key, index)
                            else if (state.draggedKey == key) state.stopDrag()
                        }
                        true
                    }
                    Key.DirectionUp -> {
                        if (isDragging && !state.isPointerDrag) {
                            if (isKeyDown) state.moveUp(index)
                            true
                        } else
                            false
                    }
                    Key.DirectionDown -> {
                        if (isDragging && !state.isPointerDrag) {
                            if (isKeyDown) state.moveDown(index)
                            true
                        } else false
                    }
                    else -> false
                }
            }
    ) {
        content(isDragging)
    }
}







